import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Spinner,
  Alert,
  Modal,
  Pagination,
  Dropdown,
  ProgressBar,
  Table,
  InputGroup,
  FormControl,
  Offcanvas,
  DropdownButton,
  Form
} from 'react-bootstrap';
import { useTasks } from '../contexts/TasksContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useChat } from '../contexts/ChatContext'; // ✅ ДОБАВЛЕНО
import TaskForm from './TaskForm';
import TaskCard from './TaskCard';
import TaskFilters from './TaskFilters';
import TaskViewModal from './TaskViewModal';
import TasksBoard from './TasksBoard';
import { usersAPI } from '../services/api.js';

const TasksTab = () => {
  const {
    tasks,
    totalTasks,
    loading,
    wsLoading,
    error,
    loadTasks,
    requestTasksList,
    createTask,
    updateTask,
    deleteTask,
    completeTask,
    assignTask: assignTaskOriginal,
    loadStats,
    getFullUserName,
    reloadTask,
    addTaskComment
  } = useTasks();

  const { isConnected } = useWebSocket();
  const { loadRooms } = useChat(); // ✅ ДОБАВЛЕНО

  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('tasks_filters');
      return saved ? JSON.parse(saved) : {
        status: '',
        priority: '',
        category: '',
        assignee_id: '',
        search: ''
      };
    } catch {
      return {
        status: '',
        priority: '',
        category: '',
        assignee_id: '',
        search: ''
      };
    }
  });

  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('tasks_sorting');
      return saved ? JSON.parse(saved) : {
        sortBy: 'created_at',
        sortOrder: 'desc'
      };
    } catch {
      return {
        sortBy: 'created_at',
        sortOrder: 'desc'
      };
    }
  });

  const [pagination, setPagination] = useState(() => {
    try {
      const saved = localStorage.getItem('tasks_pagination');
      return saved ? JSON.parse(saved) : {
        tasksPerPage: 15,
        currentPage: 1
      };
    } catch {
      return {
        tasksPerPage: 15,
        currentPage: 1
      };
    }
  });

  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('tasks_view_mode');
      const validModes = ['table', 'grid', 'board'];
      return validModes.includes(saved) ? saved : 'table';
    } catch {
      return 'table';
    }
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [viewTask, setViewTask] = useState(null);
  const [stats, setStats] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [operationResult, setOperationResult] = useState({ show: false, message: '', type: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [quickSearch, setQuickSearch] = useState('');
  
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedTaskTitle, setSelectedTaskTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignError, setAssignError] = useState('');
  
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const refreshIntervalRef = useRef(null);

  // Сохранение в localStorage
  useEffect(() => {
    localStorage.setItem('tasks_filters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem('tasks_sorting', JSON.stringify(sortConfig));
  }, [sortConfig]);

  useEffect(() => {
    localStorage.setItem('tasks_pagination', JSON.stringify(pagination));
  }, [pagination]);

  useEffect(() => {
    localStorage.setItem('tasks_view_mode', viewMode);
  }, [viewMode]);

  const tasksPerPageOptions = [6, 12, 18, 24, 30, 48];

  // Загрузка данных
  useEffect(() => {
    const skip = (pagination.currentPage - 1) * pagination.tasksPerPage;
    const limit = pagination.tasksPerPage;
    const params = { ...filters, skip, limit };

    if (isConnected) {
      requestTasksList(params);
    } else {
      loadTasks(params);
    }
    loadStatsData();
  }, [filters, sortConfig, pagination.currentPage, pagination.tasksPerPage, isConnected]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [pagination.tasksPerPage, filters, sortConfig]);

  useEffect(() => {
    if (showAssignModal) {
      loadUsers();
    }
  }, [showAssignModal]);

  // Автообновление
  useEffect(() => {
    const hasOpenModals = showCreateModal || selectedTask || viewTask || showAssignModal;
    
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    if (!hasOpenModals && !loading && !wsLoading) {
      refreshIntervalRef.current = setInterval(() => {
        console.log('🔄 Автообновление задач...');
        const skip = (pagination.currentPage - 1) * pagination.tasksPerPage;
        const limit = pagination.tasksPerPage;
        if (isConnected) {
          requestTasksList({ ...filters, skip, limit });
        } else {
          loadTasks({ ...filters, skip, limit });
        }
        setLastRefreshTime(new Date());
        setRefreshCount(prev => prev + 1);
      }, 60000);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [showCreateModal, selectedTask, viewTask, showAssignModal, loading, wsLoading, isConnected, filters, pagination]);

  const loadStatsData = async () => {
    try {
      const statsData = await loadStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await usersAPI.getUsers({ limit: 100 });
      const usersList = response.users || response.data || response;
      setUsers(Array.isArray(usersList) ? usersList : []);
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
      setAssignError('Не удалось загрузить список пользователей');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAssignClick = useCallback((taskId, taskTitle) => {
    setSelectedTaskId(taskId);
    setSelectedTaskTitle(taskTitle);
    setAssigneeId('');
    setAssignError('');
    setShowAssignModal(true);
  }, []);

  const handleAssignFromModal = useCallback(async () => {
    if (!assigneeId) {
      setAssignError('Выберите пользователя');
      return;
    }
    
    const result = await assignTaskOriginal(selectedTaskId, parseInt(assigneeId));
    
    if (result.success) {
      if (viewTask && viewTask.id === selectedTaskId) {
        setViewTask(result.task);
      }
      setShowAssignModal(false);
      setSelectedTaskId(null);
      setSelectedTaskTitle('');
      showOperationAlert('Задача успешно назначена!', 'success');
    } else {
      setAssignError(result.error || 'Не удалось назначить задачу');
    }
  }, [assigneeId, assignTaskOriginal, selectedTaskId, viewTask]);

  const getFullName = (user) => {
    if (!user) return 'Неизвестно';
    
    const firstName = user.telegram_first_name || user.first_name || '';
    const lastName = user.telegram_last_name || user.last_name || '';
    const userName = user.username || user.name || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (userName) {
      return userName;
    }
    
    return `Пользователь #${user.id}`;
  };

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleSortChange = useCallback((newSortBy) => {
    setSortConfig(prev => ({
      sortBy: newSortBy,
      sortOrder: prev.sortBy === newSortBy ? (prev.sortOrder === 'asc' ? 'desc' : 'asc') : 'desc'
    }));
  }, []);

  const handleQuickSearch = (e) => {
    setQuickSearch(e.target.value);
    if (e.target.value.trim() === '') {
      handleFilterChange({ ...filters, search: '' });
    }
  };

  const applyQuickSearch = () => {
    handleFilterChange({ ...filters, search: quickSearch });
  };

  const resetAllSettings = useCallback(() => {
    setFilters({
      status: '',
      priority: '',
      category: '',
      assignee_id: '',
      search: ''
    });
    setSortConfig({
      sortBy: 'created_at',
      sortOrder: 'desc'
    });
    setPagination({
      tasksPerPage: 15,
      currentPage: 1
    });
    setViewMode('table');
    setQuickSearch('');
  }, []);

  // Локальная сортировка текущей страницы
  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      let aValue = a[sortConfig.sortBy];
      let bValue = b[sortConfig.sortBy];
      
      if (sortConfig.sortBy.includes('_at') || sortConfig.sortBy.includes('_date')) {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortConfig.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [tasks, sortConfig]);

  const totalPages = Math.ceil(totalTasks / pagination.tasksPerPage);

  const handleCreateTask = async (taskData) => {
      console.log('📝 Creating new task with data:', taskData);
      const result = await createTask(taskData);
      
      if (result.success) {
          return {
              success: true,
              task: result.task,
              taskId: result.task.id
          };
      }
      
      return result;
  };

  const handleUpdateTask = async (taskId, taskData) => {
    const result = await updateTask(taskId, taskData);
    
    if (result.success) {
      if (viewTask && viewTask.id === taskId) {
        setViewTask(result.task);
      }
      await loadStatsData();
      showOperationAlert('Задача успешно обновлена!', 'success');
    } else {
      showOperationAlert(`Ошибка при обновлении задачи: ${result.error}`, 'error');
    }
  };

  const handleDeleteTask = async (taskId) => {
    const result = await deleteTask(taskId);
    
    if (result.success) {
      if (viewTask && viewTask.id === taskId) {
        setViewTask(null);
      }
      await loadStatsData();
      
      if (tasks.length === 0 && pagination.currentPage > 1) {
        setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
      }
      
      showOperationAlert('Задача успешно удалена!', 'success');
    } else {
      showOperationAlert(`Ошибка при удалении задачи: ${result.error}`, 'error');
    }
  };

  const handleCompleteTask = async (taskId, actualHours) => {
    const result = await completeTask(taskId, actualHours);
    
    if (result.success) {
      if (viewTask && viewTask.id === taskId) {
        setViewTask(result.task);
      }
      await loadStatsData();
      showOperationAlert('Задача успешно завершена!', 'success');
    } else {
      showOperationAlert(`Ошибка при завершении задачи: ${result.error}`, 'error');
    }
  };

  const showOperationAlert = (message, type) => {
    setOperationResult({ show: true, message, type });
    setTimeout(() => setOperationResult({ show: false, message: '', type: '' }), 
      type === 'success' ? 3000 : 5000);
  };

  const getStatusBadgeVariant = (status) => {
    const variants = {
      'open': 'secondary',
      'assigned': 'info',
      'in_progress': 'warning',
      'completed': 'success',
      'cancelled': 'danger',
      'reopened': 'primary'
    };
    return variants[status] || 'secondary';
  };

  const getPriorityBadgeVariant = (priority) => {
    const variants = {
      'low': 'success',
      'medium': 'info',
      'high': 'warning',
      'urgent': 'danger'
    };
    return variants[priority] || 'secondary';
  };

  const formatStatus = (status) => {
    const statusMap = {
      'open': 'Открыта',
      'assigned': 'Назначена',
      'in_progress': 'В работе',
      'completed': 'Выполнена',
      'cancelled': 'Отменена',
      'reopened': 'Переоткрыта'
    };
    return statusMap[status] || status;
  };

  const formatPriority = (priority) => {
    const priorityMap = {
      'low': 'Низкий',
      'medium': 'Средний',
      'high': 'Высокий',
      'urgent': 'Срочный'
    };
    return priorityMap[priority] || priority;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isOverdue = (task) => {
    return task.due_date && 
           new Date(task.due_date) < new Date() && 
           !['completed', 'cancelled'].includes(task.status);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, pagination.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
        disabled={pagination.currentPage === 1}
      />
    );

    if (start > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setPagination(prev => ({ ...prev, currentPage: 1 }))}>
          1
        </Pagination.Item>
      );
      if (start > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" />);
    }

    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === pagination.currentPage}
          onClick={() => setPagination(prev => ({ ...prev, currentPage: i }))}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => setPagination(prev => ({ ...prev, currentPage: totalPages }))}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(totalPages, prev.currentPage + 1) }))}
        disabled={pagination.currentPage === totalPages}
      />
    );

    return <Pagination className="mb-0">{items}</Pagination>;
  };

  const getRefreshButtonState = () => {
    const hasOpenModals = showCreateModal || selectedTask || viewTask || showAssignModal;
    
    if (hasOpenModals) {
      return {
        variant: "outline-secondary",
        title: "Обновить (автообновление приостановлено)"
      };
    } else {
      return {
        variant: (loading || wsLoading) ? "outline-secondary" : "outline-primary",
        title: "Обновить (автообновление: 1 мин)"
      };
    }
  };

  const effectiveLoading = isConnected ? wsLoading : loading;

  if (effectiveLoading && tasks.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted mb-2">Загрузка задач...</p>
          {loadingProgress > 0 && (
            <div className="mt-3 w-50 mx-auto">
              <ProgressBar 
                now={loadingProgress} 
                label={`${loadingProgress}%`}
                variant="primary"
                animated
              />
            </div>
          )}
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="tasks-tab">
      {/* Панель управления */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body className="py-3">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div className="d-flex flex-wrap gap-2 w-100 w-md-auto">
              <Button
                variant="outline-primary"
                onClick={() => setShowFilters(true)}
                className="d-md-none"
              >
                <i className="bi bi-funnel"></i>
                <span className="d-none d-sm-inline ms-1">Фильтры</span>
              </Button>

              <div className="btn-group">
                <Button
                  variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('table')}
                  size="sm"
                  title="Таблица"
                >
                  <i className="bi bi-table"></i>
                  <span className="d-none d-md-inline ms-1">Таблица</span>
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('grid')}
                  size="sm"
                  title="Сетка"
                >
                  <i className="bi bi-grid-3x3-gap"></i>
                  <span className="d-none d-md-inline ms-1">Сетка</span>
                </Button>
                <Button
                  variant={viewMode === 'board' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('board')}
                  size="sm"
                  title="Доска"
                >
                  <i className="bi bi-kanban"></i>
                  <span className="d-none d-md-inline ms-1">Доска</span>
                </Button>
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant={getRefreshButtonState().variant}
                  onClick={() => {
                    const skip = (pagination.currentPage - 1) * pagination.tasksPerPage;
                    const limit = pagination.tasksPerPage;
                    if (isConnected) {
                      requestTasksList({ ...filters, skip, limit });
                    } else {
                      loadTasks({ ...filters, skip, limit });
                    }
                  }}
                  title={getRefreshButtonState().title}
                  disabled={effectiveLoading}
                  size="sm"
                >
                  <i className={`bi bi-arrow-clockwise ${effectiveLoading ? 'spin' : ''}`}></i>
                  {(showCreateModal || selectedTask || viewTask || showAssignModal) && (
                    <span className="ms-1">⏸️</span>
                  )}
                </Button>
                <Button
                  variant="outline-warning"
                  onClick={resetAllSettings}
                  title="Сбросить настройки"
                  size="sm"
                >
                  <i className="bi bi-arrow-counterclockwise"></i>
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                  className="d-flex align-items-center"
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  <span className="d-none d-md-inline">Создать</span>
                </Button>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="mb-3" dismissible>
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {operationResult.show && (
        <Alert 
          variant={operationResult.type === 'success' ? 'success' : 'danger'} 
          className="mb-3"
          dismissible
          onClose={() => setOperationResult({ show: false, message: '', type: '' })}
        >
          <i className={`bi bi-${operationResult.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
          {operationResult.message}
        </Alert>
      )}

      <Row className="g-3">
        {/* Боковая панель с фильтрами для десктопа */}
        <Col md={2} className="d-none d-md-block">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom">
              <h6 className="mb-0">
                <i className="bi bi-funnel me-2"></i>
                Фильтры
              </h6>
            </Card.Header>
            <Card.Body>
              <TaskFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                vertical={true}
              />
              <div className="mt-4 pt-3 border-top">
                <Button
                  variant="outline-secondary"
                  onClick={resetAllSettings}
                  className="w-100"
                >
                  Сбросить все фильтры
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={10}>
          {/* Панель управления отображением */}
          {viewMode !== 'board' && (
            <Card className="mb-3 border-0 shadow-sm">
              <Card.Body className="py-2">
                <Row className="align-items-center">
                  <Col md={6} className="mb-2 mb-md-0">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <div className="d-flex align-items-center">
                        <span className="text-muted me-2">Показать:</span>
                        <DropdownButton
                          id="dropdown-tasks-per-page"
                          title={pagination.tasksPerPage}
                          variant="outline-secondary"
                          size="sm"
                        >
                          {tasksPerPageOptions.map(option => (
                            <Dropdown.Item 
                              key={option}
                              active={pagination.tasksPerPage === option}
                              onClick={() => setPagination(prev => ({ ...prev, tasksPerPage: option }))}
                            >
                              {option} задач
                            </Dropdown.Item>
                          ))}
                        </DropdownButton>
                      </div>
                      
                      <div className="d-flex align-items-center">
                        <span className="text-muted mx-3">Сортировка:</span>
                        <DropdownButton
                          id="dropdown-sort"
                          title={
                            <>
                              <i className={`bi bi-sort-${sortConfig.sortOrder === 'asc' ? 'up' : 'down'}-alt me-1`}></i>
                              {sortConfig.sortBy === 'created_at' && 'Дата создания'}
                              {sortConfig.sortBy === 'title' && 'Название'}
                              {sortConfig.sortBy === 'priority' && 'Приоритет'}
                              {sortConfig.sortBy === 'due_date' && 'Срок'}
                            </>
                          }
                          variant="outline-secondary"
                          size="sm"
                        >
                          <Dropdown.Item 
                            active={sortConfig.sortBy === 'created_at'}
                            onClick={() => handleSortChange('created_at')}
                          >
                            Дата создания
                          </Dropdown.Item>
                          <Dropdown.Item 
                            active={sortConfig.sortBy === 'title'}
                            onClick={() => handleSortChange('title')}
                          >
                            Название
                          </Dropdown.Item>
                          <Dropdown.Item 
                            active={sortConfig.sortBy === 'priority'}
                            onClick={() => handleSortChange('priority')}
                          >
                            Приоритет
                          </Dropdown.Item>
                          <Dropdown.Item 
                            active={sortConfig.sortBy === 'due_date'}
                            onClick={() => handleSortChange('due_date')}
                          >
                            Срок выполнения
                          </Dropdown.Item>
                        </DropdownButton>
                      </div>
                    </div>
                  </Col>
                  
                  <Col md={6}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="text-muted small">
                        Задачи <strong>{((pagination.currentPage - 1) * pagination.tasksPerPage) + 1}</strong>
                        -<strong>{Math.min(pagination.currentPage * pagination.tasksPerPage, totalTasks)}</strong> из <strong>{totalTasks}</strong>
                      </div>
                      {renderPagination()}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {sortedTasks.length === 0 ? (
            <Card className="text-center border-0 shadow-sm">
              <Card.Body className="py-5">
                <i className="bi bi-inbox display-1 text-muted mb-3 opacity-50"></i>
                <h5 className="text-muted">Задачи не найдены</h5>
                <p className="text-muted mb-4">
                  {Object.values(filters).some(f => f) 
                    ? 'Измените параметры фильтрации' 
                    : 'Создайте первую задачу'
                  }
                </p>
                <Button
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                  className="px-4"
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Создать задачу
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <>
              {viewMode === 'board' ? (
                <TasksBoard
                  onView={setViewTask}
                  getStatusBadgeVariant={getStatusBadgeVariant}
                  getPriorityBadgeVariant={getPriorityBadgeVariant}
                />
              ) : viewMode === 'table' ? (
                <Card className="border-0 shadow-sm">
                  <div className="table-responsive" style={{ minHeight: '400px' }}>
                    <Table hover className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '50px' }} className="ps-3">#</th>
                          <th>Задача</th>
                          <th style={{ width: '100px' }}>Статус</th>
                          <th style={{ width: '100px' }}>Приоритет</th>
                          <th style={{ width: '120px' }} className="d-none d-lg-table-cell">Категория</th>
                          <th style={{ width: '140px' }} className="d-none d-md-table-cell">Исполнитель</th>
                          <th style={{ width: '100px' }}>Срок</th>
                          <th style={{ width: '150px' }} className="text-center">Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedTasks.map(task => (
                          <tr key={task.id} className={isOverdue(task) ? 'table-warning' : ''}>
                            <td className="ps-3">
                              <Badge bg="secondary" className="fw-normal">#{task.id}</Badge>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <div 
                                  className="fw-semibold cursor-pointer hover-primary"
                                  onClick={() => setViewTask(task)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {task.title.length > 80 ? `${task.title.substring(0, 80)}...` : task.title}
                                </div>
                                {task.description && (
                                  <small className="text-muted">
                                    {task.description.length > 70 ? `${task.description.substring(0, 70)}...` : task.description}
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <Badge bg={getStatusBadgeVariant(task.status)} className="w-100">
                                {formatStatus(task.status)}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={getPriorityBadgeVariant(task.priority)}>
                                {formatPriority(task.priority)}
                              </Badge>
                            </td>
                            <td className="d-none d-lg-table-cell">
                              <span className="badge bg-light text-dark border">
                                {task.category || '—'}
                              </span>
                            </td>
                            <td className="d-none d-md-table-cell">
                              <small>
                                {task.assignee ? getFullUserName(task.assignee) : 'Не назначен'}
                              </small>
                            </td>
                            <td>
                              <div className={`small ${isOverdue(task) ? 'text-danger fw-bold' : ''}`}>
                                {formatDate(task.due_date)}
                                {isOverdue(task) && (
                                  <i className="bi bi-exclamation-triangle ms-1"></i>
                                )}
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-1">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => setViewTask(task)}
                                  title="Просмотр"
                                >
                                  <i className="bi bi-eye"></i>
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => setSelectedTask(task)}
                                  title="Редактировать"
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                                
                                <DropdownButton
                                  id={`dropdown-actions-${task.id}`}
                                  title={<i className="bi bi-three-dots-vertical"></i>}
                                  variant="outline-secondary"
                                  size="sm"
                                  align="end"
                                >
                                  <Dropdown.Item onClick={() => setSelectedTask(task)}>
                                    <i className="bi bi-pencil me-2"></i>
                                    Редактировать
                                  </Dropdown.Item>
                                  {task.status !== 'completed' && (
                                    <>
                                      <Dropdown.Item onClick={() => {
                                        const actualHours = prompt('Введите фактическое время выполнения (часы):', '');
                                        if (actualHours !== null) {
                                          handleCompleteTask(task.id, actualHours || undefined);
                                        }
                                      }}>
                                        <i className="bi bi-check-lg me-2"></i>
                                        Завершить
                                      </Dropdown.Item>
                                      <Dropdown.Item 
                                        onClick={() => handleAssignClick(task.id, task.title)}
                                      >
                                        <i className="bi bi-person-plus me-2"></i>
                                        Назначить
                                      </Dropdown.Item>
                                    </>
                                  )}
                                  <Dropdown.Divider />
                                  <Dropdown.Item 
                                    onClick={() => {
                                      if (window.confirm(`Вы точно хотите удалить задачу "${task.title}"?`)) {
                                        handleDeleteTask(task.id);
                                      }
                                    }}
                                    className="text-danger"
                                  >
                                    <i className="bi bi-trash me-2"></i>
                                    Удалить
                                  </Dropdown.Item>
                                </DropdownButton>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  
                  {totalPages > 1 && (
                    <Card.Footer className="bg-white border-top py-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-muted small">
                          Страница <strong>{pagination.currentPage}</strong> из <strong>{totalPages}</strong>
                        </div>
                        {renderPagination()}
                        <div className="text-muted small">
                          Всего задач: <strong>{totalTasks}</strong>
                        </div>
                      </div>
                    </Card.Footer>
                  )}
                </Card>
              ) : (
                <Row className="g-3">
                  {sortedTasks.map(task => (
                    <Col key={task.id} xl={4} lg={6} md={6} sm={12}>
                      <TaskCard
                        task={task}
                        onView={() => setViewTask(task)}
                        onEdit={() => setSelectedTask(task)}
                        onDelete={handleDeleteTask}
                        onComplete={handleCompleteTask}
                        onAssign={assignTaskOriginal}
                        getStatusBadgeVariant={getStatusBadgeVariant}
                        getPriorityBadgeVariant={getPriorityBadgeVariant}
                        compact={true}
                      />
                    </Col>
                  ))}
                  
                  {totalPages > 1 && (
                    <Col xs={12}>
                      <Card className="border-0 shadow-sm mt-3">
                        <Card.Body className="py-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="text-muted">
                              Страница <strong>{pagination.currentPage}</strong> из <strong>{totalPages}</strong>
                            </div>
                            {renderPagination()}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  )}
                </Row>
              )}
            </>
          )}
        </Col>
      </Row>

      {/* Модальные окна */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
          <Modal.Header closeButton className="border-bottom">
              <Modal.Title className="d-flex align-items-center">
                  <i className="bi bi-plus-circle text-primary me-2"></i>
                  Новая задача
              </Modal.Title>
          </Modal.Header>
          <Modal.Body>
              <TaskForm
                  onSubmit={handleCreateTask}
                  onSuccess={(result) => {
                      console.log('✅ Task created successfully with result:', result);
                      setShowCreateModal(false);
                      loadStatsData();
                      loadRooms(); // ✅ ИСПРАВЛЕНО: было loadChatRooms()
                      
                      if (result.task) {
                          setViewTask(result.task);
                      }
                      
                      // ОБНОВЛЯЕМ СПИСОК ЗАДАЧ НА ТЕКУЩЕЙ СТРАНИЦЕ
                      const skip = (pagination.currentPage - 1) * pagination.tasksPerPage;
                      const limit = pagination.tasksPerPage;
                      loadTasks({ ...filters, skip, limit });
                      
                      setPagination(prev => ({ ...prev, currentPage: 1 }));
                      showOperationAlert('Задача успешно создана!', 'success');
                  }}
                  onCancel={() => setShowCreateModal(false)}
                  loading={effectiveLoading}
              />
          </Modal.Body>
      </Modal>

      <Modal show={!!selectedTask} onHide={() => setSelectedTask(null)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-pencil-square text-primary me-2"></i>
            Редактирование задачи
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <TaskForm
              task={selectedTask}
              onSubmit={(data) => handleUpdateTask(selectedTask.id, data)}
              onCancel={() => setSelectedTask(null)}
              loading={effectiveLoading}
            />
          )}
        </Modal.Body>
      </Modal>

      <TaskViewModal
        key={viewTask?.id}
        task={viewTask}
        onClose={() => setViewTask(null)}
        onEdit={(task) => {
          setViewTask(null);
          setSelectedTask(task);
        }}
        onDelete={handleDeleteTask}
        onComplete={handleCompleteTask}
        onAssign={assignTaskOriginal}
        getStatusBadgeVariant={getStatusBadgeVariant}
        getPriorityBadgeVariant={getPriorityBadgeVariant}
      />

      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-plus me-2 text-info"></i>
            Назначение задачи
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Назначить задачу "<strong>{selectedTaskTitle}</strong>" пользователю:</p>
          
          {assignError && (
            <Alert variant="danger" className="py-2 mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {assignError}
            </Alert>
          )}
          
          <Form.Group>
            <Form.Label>Выберите пользователя</Form.Label>
            {loadingUsers ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Загрузка пользователей...</span>
              </div>
            ) : (
              <Form.Select
                value={assigneeId}
                onChange={(e) => {
                  setAssigneeId(e.target.value);
                  setAssignError('');
                }}
                required
              >
                <option value="">Выберите исполнителя</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {getFullName(user)} (ID: {user.id})
                    {user.telegram_username && ` - @${user.telegram_username}`}
                  </option>
                ))}
              </Form.Select>
            )}
            <Form.Text className="text-muted">
              Выберите пользователя, которому хотите назначить задачу
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Отмена
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignFromModal}
            disabled={!assigneeId || loadingUsers}
          >
            <i className="bi bi-person-check me-2"></i>
            Назначить
          </Button>
        </Modal.Footer>
      </Modal>

      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>
            <i className="bi bi-funnel me-2"></i>
            Фильтры задач
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <TaskFilters
            filters={filters}
            onFilterChange={(newFilters) => {
              handleFilterChange(newFilters);
              setShowFilters(false);
            }}
            vertical={true}
          />
          <div className="mt-4 pt-3 border-top">
            <Button
              variant="outline-secondary"
              onClick={() => {
                resetAllSettings();
                setShowFilters(false);
              }}
              className="w-100"
            >
              Сбросить все фильтры
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
      
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default TasksTab;